import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import variableList from './variableList.json';

const CODE_SX = { fontFamily: 'monospace', wordBreak: 'break-all' } as const;

function CopyButton({ code, label }: { code: string; label: string }) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };
  return (
    <Tooltip title='Copy'>
      <IconButton size='small' color='primary' aria-label={label} onClick={handleCopy}>
        <ContentCopyOutlined fontSize='small' />
      </IconButton>
    </Tooltip>
  );
}

export function PaneHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant='overline'
      component='div'
      color='text.secondary'
      sx={{ textAlign: 'center', lineHeight: 1.75 }}
    >
      {children}
    </Typography>
  );
}

/** Template variables available in landing-page content, each with a copy button. */
export default function VariablePane() {
  const [search, setSearch] = React.useState('');
  const filtered = React.useMemo(
    () => variableList.filter((variable) => variable.name.toLowerCase().includes(search.toLowerCase())),
    [search]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <PaneHeading>Variables</PaneHeading>
        <TextField
          size='small'
          fullWidth
          placeholder='Search...'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            htmlInput: { 'aria-label': 'Search variables' },
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined fontSize='small' />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {filtered.map((variable) => (
          <ListItem
            key={variable.code}
            divider
            secondaryAction={<CopyButton code={variable.code} label={`Copy ${variable.name}`} />}
          >
            <ListItemText
              primary={variable.name}
              secondary={variable.code}
              slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'caption', sx: CODE_SX } }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getFlat = (obj: Record<string, unknown>): Array<[string, string]> =>
  Object.entries(obj).flatMap(([key, value]): Array<[string, string]> => {
    if (isObject(value)) return getFlat(value).map(([k, v]) => [`${key}.${k}`, v] as [string, string]);
    if (Array.isArray(value)) return [[key, value.join(', ')]];
    return [[key, String(value)]];
  });

interface CustomProperty {
  set: string;
  name: string;
  value: string;
  code: string;
}

function CustomPropertyItem({ property }: { property: CustomProperty }) {
  const { set, name, value, code } = property;
  return (
    <ListItem divider secondaryAction={<CopyButton code={code} label={`Copy ${set} ${name}`} />}>
      <ListItemText
        primary={
          <>
            <Typography component='span' variant='body2' color='text.secondary'>
              {set} ·{' '}
            </Typography>
            <Typography component='span' variant='body2' sx={{ fontWeight: 500 }}>
              {name}
            </Typography>
            <Typography component='span' variant='body2' sx={{ ml: 1 }}>
              {value}
            </Typography>
          </>
        }
        secondary={code}
        slotProps={{ secondary: { variant: 'caption', sx: CODE_SX } }}
      />
    </ListItem>
  );
}

/** Flattened custom-property values with copyable template codes. Used by the Custom Properties preview. */
export function CustomPropertyPane({ values }: { values: Record<string, unknown> }) {
  const flat = Object.fromEntries(getFlat(values));
  return (
    <Box>
      <PaneHeading>Properties</PaneHeading>
      <List dense disablePadding sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {Object.entries(flat)
          .filter(([key]) => key.endsWith('.value'))
          .map(([key, value]) => {
            const parts = key.split('.');
            return (
              <CustomPropertyItem
                key={key}
                property={{ set: parts[1] ?? '', name: parts[2] ?? '', value, code: `[{${key}}]` }}
              />
            );
          })}
      </List>
    </Box>
  );
}
